import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { FileText, CheckCircle, Briefcase, BookOpen, UserCheck, ShieldAlert, Scale, Shield, XCircle, Globe, RefreshCw, Mail } from "lucide-react"
import { SEO } from "@/components/SEO"

const TermsOfUse = () => {
    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            <SEO
                title="Terms of Use | RSA Tech Group"
                description="Review the terms and conditions governing the use of RSA Tech Group's website and services."
            />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-20 overflow-hidden bg-gradient-to-b from-[#0F172A] to-[#09090b]">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px] opacity-40 mix-blend-screen" />
                    <div className="absolute bottom-0 left-[-100px] w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[100px] opacity-30" />
                </div>
                <div className="section-container relative z-10 text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
                        Terms of Use
                    </h1>
                    <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
                        Please read these terms carefully before using our website and services.
                    </p>
                    <div className="mt-8 text-sm text-gray-400 font-medium tracking-wide uppercase">
                        Last Updated: January 2026
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <section className="py-24 bg-slate-50 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('/digital-transform-bg-rectangles.png')] bg-cover bg-center opacity-[0.03]" />

                <div className="section-container relative z-10 max-w-4xl mx-auto">
                    <div className="bg-white/80 backdrop-blur-md border border-slate-200 rounded-2xl p-8 md:p-12 shadow-sm">

                        {/* 1. Introduction */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">1. Introduction</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    These Terms of Use ("Terms") govern your access to and use of the website, platforms, and services provided by RSA Tech Group and its subsidiaries ("Company," "we," "our," or "us"). By accessing or using our services, you agree to be bound by these Terms.
                                </p>
                                <p>
                                    If you do not agree to these Terms, please do not access or use our website or services.
                                </p>
                            </div>
                        </div>

                        {/* 2. Acceptance of Terms */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                                    <CheckCircle className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">2. Acceptance of Terms</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    By accessing this website or using any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a> and <a href="/cookie-policy" className="text-blue-600 hover:underline">Cookie Policy</a>.
                                </p>
                                <p>
                                    We reserve the right to modify these Terms at any time. Continued use of our services after any modifications constitutes your acceptance of the revised Terms.
                                </p>
                            </div>
                        </div>

                        {/* 3. Use of Services */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                                    <Briefcase className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">3. Use of Services</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>You agree to use our website and services only for lawful purposes and in accordance with these Terms. You may not use our services:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>In any way that violates any applicable federal, state, local, or international law or regulation.</li>
                                    <li>To transmit, or procure the sending of, any unsolicited or unauthorized advertising or promotional material.</li>
                                    <li>To impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or entity.</li>
                                    <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the services.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 4. Intellectual Property */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
                                    <BookOpen className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">4. Intellectual Property</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    The website and its entire contents, features, and functionality — including but not limited to all information, software, text, displays, images, video, and audio, and the design, selection, and arrangement thereof — are owned by RSA Tech Group, its licensors, or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
                                </p>
                                <p>
                                    You must not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our website without prior written consent.
                                </p>
                            </div>
                        </div>

                        {/* 5. User Responsibilities */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600">
                                    <UserCheck className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">5. User Responsibilities</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>As a user of our services, you are responsible for:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Providing accurate and complete information when interacting with our services.</li>
                                    <li>Maintaining the confidentiality of any account credentials or access tokens.</li>
                                    <li>Ensuring that your use of our services complies with all applicable laws and regulations.</li>
                                    <li>Promptly notifying us of any unauthorized use of your account or any other breach of security.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 6. Prohibited Activities */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-600">
                                    <ShieldAlert className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">6. Prohibited Activities</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>You are expressly prohibited from:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Using any robot, spider, or other automatic device, process, or means to access the website for any purpose, including monitoring or copying any of the material.</li>
                                    <li>Introducing any viruses, trojan horses, worms, logic bombs, or other material that is malicious or technologically harmful.</li>
                                    <li>Attempting to gain unauthorized access to, interfere with, damage, or disrupt any parts of the website or any server, computer, or database connected to the website.</li>
                                    <li>Attacking the website via a denial-of-service attack or a distributed denial-of-service attack.</li>
                                    <li>Otherwise attempting to interfere with the proper working of the website.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 7. Limitation of Liability */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center text-cyan-600">
                                    <Scale className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">7. Limitation of Liability</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    To the fullest extent permitted by applicable law, RSA Tech Group shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
                                </p>
                                <p>
                                    The website and all information, content, materials, products, and services included on or otherwise made available to you through the website are provided on an "as is" and "as available" basis, unless otherwise specified in writing.
                                </p>
                            </div>
                        </div>

                        {/* 8. Indemnification */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center text-pink-600">
                                    <Shield className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">8. Indemnification</h2>
                            </div>
                            <p className="text-slate-600 leading-relaxed pl-3 md:pl-14">
                                You agree to defend, indemnify, and hold harmless RSA Tech Group, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the website.
                            </p>
                        </div>

                        {/* 9. Termination */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600">
                                    <XCircle className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">9. Termination</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    We may terminate or suspend your access to our services immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms.
                                </p>
                                <p>
                                    Upon termination, your right to use the services will immediately cease. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                                </p>
                            </div>
                        </div>

                        {/* 10. Governing Law */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600">
                                    <Globe className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">10. Governing Law</h2>
                            </div>
                            <p className="text-slate-600 leading-relaxed pl-3 md:pl-14">
                                These Terms shall be governed and construed in accordance with the laws of the State of New Jersey, United States, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
                            </p>
                        </div>

                        {/* 11. Changes to Terms */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600">
                                    <RefreshCw className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">11. Changes to Terms</h2>
                            </div>
                            <p className="text-slate-600 leading-relaxed pl-3 md:pl-14">
                                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our services after any revisions become effective, you agree to be bound by the revised terms.
                            </p>
                        </div>

                        {/* 12. Contact Us */}
                        <div className="mt-16 pt-12 border-t border-slate-100">
                            <h2 className="text-2xl font-bold text-slate-900 mb-6">12. Contact Us</h2>
                            <p className="text-slate-600 mb-6">
                                If you have any questions about these Terms of Use, please contact us:
                            </p>
                            <div className="flex flex-col gap-2 pl-3 md:pl-0">
                                <div className="font-bold text-slate-900">RSA Tech Group</div>
                                <div className="flex items-center gap-3 text-slate-800 font-medium">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                        <Mail className="w-4 h-4" />
                                    </div>
                                    <a href="mailto:info@rsatechgroup.com" className="hover:text-blue-600 transition-colors">info@rsatechgroup.com</a>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default TermsOfUse
